// R-131-FIN-DECISION-A — large nuclear (AP1000-class) pathway preset (NREL ATB).

import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type { Pathway } from '../types';
import {
  DEFAULT_BASE_WACC_PCT,
  annualMwhFromCf,
  buildCapexBlock,
  buildStandardBtmPhases,
  capexFromPerKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveNameplateMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';

/** AP1000-class large LWR — NREL ATB 2024 utility-scale nuclear NOAK band. */
export const NUCLEAR_LARGE_BENCHMARKS = {
  capexPerKwUsd: 5800,
  fixedOmPerKwYearUsd: 90,
  variableOmPerMwhUsd: 2,
  fuelCostPerMwhUsd: 8,
  designLifeYears: 60,
  availabilityPct: 92,
  capacityFactorPct: 92,
  constructionMonths: 72,
  riskPremiumBps: 100,
  refurbYearFromCod: 30,
  refurbPctOfCapex: 0.2,
  decommPctOfCapex: 0.12,
  contingencyPct: 12,
} as const;

export function presetFor(
  intent: ProjectIntent,
  _siteContext: SiteContext,
  options?: PathwayPresetOptions,
): Pathway {
  // Generation NAMEPLATE: facility LOAD grossed up to a plant that SERVES the
  // firm 24/7 load (nuclear ≈ load × 1.24). Capex / energy / O&M ride this; the
  // load is carried separately for load-tracking costs.
  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const capacityMw = resolveNameplateMw(intent, 'nuclear-large');
  const startDate = resolveProjectStartDate(intent);
  const b = NUCLEAR_LARGE_BENCHMARKS;

  const baseCapexUsd = capexFromPerKw(capacityMw, b.capexPerKwUsd);
  const decommissioningUsd = baseCapexUsd * b.decommPctOfCapex;
  const refurbUsd = baseCapexUsd * b.refurbPctOfCapex;
  const annualMwh = annualMwhFromCf(capacityMw, b.capacityFactorPct);
  const annualOpexUsd = computeAnnualOpexUsd(
    capacityMw,
    b.fixedOmPerKwYearUsd,
    b.variableOmPerMwhUsd,
    b.fuelCostPerMwhUsd,
    annualMwh,
  );

  const capex = buildCapexBlock(startDate, b.constructionMonths, baseCapexUsd, {
    contingencyPct: b.contingencyPct,
  });

  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(b.constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology('nuclear-large'),
    name: 'BTM Nuclear Large (AP1000-class)',
    classification: 'btm',
    technology: 'nuclear-large',
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    phases: buildStandardBtmPhases({
      startDate,
      constructionMonths: b.constructionMonths,
      designLifeYears: b.designLifeYears,
      totalCapexUsd: baseCapexUsd,
      decommissioningUsd,
      annualMwh,
      annualOpexUsd,
    }),
    capex,
    opex: {
      fixedOmPerKwYearUsd: b.fixedOmPerKwYearUsd,
      variableOmPerMwhUsd: b.variableOmPerMwhUsd,
      fuelCostPerMwhUsd: b.fuelCostPerMwhUsd,
      refurbishmentEvents: [
        {
          yearFromCod: b.refurbYearFromCod,
          usd: refurbUsd,
          description: 'Mid-life major component replacement (20% of CAPEX)',
        },
      ],
    },
    // VALUE-CONSISTENT credit — same value-of-served-load as every other
    // pathway (incl. grid). See valueOfServedLoadRevenue in shared.ts.
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'commercial-but-evolving',
      regulatoryRisk: 'high',
      fuelPriceRisk: 'none',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
    },
    carbon: {
      tCo2ePerMwh: 0,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd,
      decommissioningYear: codYear + b.designLifeYears,
    },
  };

  return mergePathway(base, options?.overrides);
}
